import React, { useState, useEffect } from "react";
import _ from "lodash";
import {
  Form,
  Dropdown,
  Segment,
  Grid,
  Header,
  Button,
  Modal,
  Loader,
  Dimmer,
} from "semantic-ui-react";
import Auth from "@aws-amplify/auth";
import dvdlocator from "../../apis/dvdlocator";
import ColumnReorder from "./ColumnReorder";
import { masterListingColumnSet } from "../database/Pricetool/ptListingColumns";
import { masterInventoryColumnSet } from "../database/Pricetool/ptInventoryColumns";
import {
  extractTooltipOrAltText,
  formatSQLColumnString,
} from "../utilities/formatters/columnSetForm";
import "./columnSets.css";
import { getColumnSetsForUserId } from "../../apis/columnSets";

const ColumnSets = () => {
  const [userId, setUserId] = useState(null);
  const [listingColumns, setListingColumns] = useState([]);
  const [inventoryColumns, setInventoryColumns] = useState([]);
  const [selectedColumnSet, setSelectedColumnSet] = useState("");
  const [selectedShareRecipient, setSelectedShareRecipient] = useState("");
  const [selectedProductColumn, setSelectedProductColumn] = useState("");
  const [selectedInventoryColumn, setSelectedInventoryColumn] = useState("");
  const [usernames, setUsernames] = useState([]);
  const [columnSets, setColumnSets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const grid = 2;
  const dropdownStyle = { marginTop: "10px", marginBottom: "10px" };

  const loadColumnSets = async (currentUserId) => {
    const columnSets = await getColumnSetsForUserId(currentUserId);
    setColumnSets(columnSets);
  };

  const loadUsernames = async (currentUserId) => {
    try {
      const response = await dvdlocator.get("/nosql/get-usernames");
      const data = response.data;
      setUsernames(data.filter((username) => username !== currentUserId));
    } catch (error) {
      console.error("Error fetching usernames:", error);
    }
  };

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const currentUserId = currentUser.attributes?.email.split("@")[0];

        setUserId(currentUserId);

        await loadColumnSets(currentUserId);
        await loadUsernames(currentUserId);
      } catch (error) {
        console.error("Error getting authenticated user:", error);
      }
    };

    initializeComponent();
  }, []);

  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: "none",
    padding: grid * 2,
    borderRadius: "3px",
    margin: `0 0 ${grid}px 0`,
    width: "125px",
    flexShrink: 0,
    background: isDragging ? "lightgreen" : "lightgrey",
    ...draggableStyle,
  });

  const getListStyle = (isDraggingOver, itemsLength) => ({
    background: isDraggingOver ? "lightblue" : "none",
    display: "flex",
    padding: grid,
    overflowX: "scroll",
    gap: "5px",
  });

  const removeProductColumn = (id) => {
    setListingColumns(listingColumns.filter((col) => col.id !== id));
  };

  const removeInventoryColumn = (id) => {
    setInventoryColumns(inventoryColumns.filter((col) => col.id !== id));
  };

  const addProductColumn = (column) => {
    const exists = listingColumns.find((col) => col.id === column.data);
    if (exists) return;

    setListingColumns([
      ...listingColumns,
      { id: column.data, content: column.title },
    ]);
  };

  const addInventoryColumn = (column) => {
    const exists = inventoryColumns.find((col) => col.id === column.data);
    if (exists) return;

    setInventoryColumns([
      ...inventoryColumns,
      { id: column.data, content: column.title },
    ]);
  };

  const getSelectedColsetRenderableList = (colSet, masterColSet) => {
    return colSet
      .map((name) => {
        const column = masterColSet.find((row) => row.data === name);
        if (!column) return null;

        return {
          id: column.data,
          content: column.title,
        };
      })
      .filter((col) => col);
  };

  const deleteColumnSet = () => {
    if (!selectedColumnSet) {
      setErrorMessage("You must select a column set");
      setShowErrorModal(true);
    } else {
      const colSetToDelete = selectedColumnSet;
      const cleanedColumnSets = columnSets.filter(
        (colSet) => colSet.columnSetName !== colSetToDelete
      );

      const accepted = window.confirm(
        `Do you want to delete the column set '${colSetToDelete}'?`
      );

      if (!accepted) return;

      setIsLoading(true);
      setLoadingMessage("Deleting column set...");

      dvdlocator
        .delete(`/nosql/${userId}/column-sets/${colSetToDelete}`)
        .then(() => {
          setSelectedColumnSet("");
          setListingColumns([]);
          setInventoryColumns([]);
          setColumnSets(cleanedColumnSets);
          setSuccessMessage(`${colSetToDelete} successfully deleted!`);
          setShowSuccessModal(true);
        })
        .catch((err) => {
          setErrorMessage(err.message);
          setShowErrorModal(true);
        })
        .finally(() => {
          setIsLoading(false);
          setLoadingMessage("");
        });
    }
  };

  const displayColumnSet = (columnSetName) => {
    const colSet = columnSets.find(
      (set) => set.columnSetName === columnSetName
    );

    const newListingColumns = getSelectedColsetRenderableList(
      colSet.columns.listingColumns,
      masterListingColumnSet
    );
    const newInventoryColumns = getSelectedColsetRenderableList(
      colSet.columns.inventoryColumns,
      masterInventoryColumnSet
    );

    setSelectedColumnSet(columnSetName);
    setListingColumns(newListingColumns);
    setInventoryColumns(newInventoryColumns);
  };

  const onShareSubmit = () => {
    if (!selectedColumnSet) {
      setErrorMessage("You must select a column set");
      setShowErrorModal(true);
    } else if (!selectedShareRecipient) {
      setErrorMessage("You must select a user to share column set with");
      setShowErrorModal(true);
    } else {
      setIsLoading(true);
      setLoadingMessage("Sharing column set...");

      dvdlocator
        .post("/nosql/share-column-sets", {
          sender: userId,
          recipient: selectedShareRecipient,
          columnSet: selectedColumnSet,
        })
        .then((response) => {
          // Handle successful response
          if (response.status === 200) {
            setSuccessMessage(
              response.data.message ||
                response.data ||
                "Column set shared successfully!"
            );
            setShowSuccessModal(true);
          }
        })
        .catch((error) => {
          // Handle different error statuses
          let errorMsg = "An unexpected error occurred";

          if (error.response) {
            const { status, data } = error.response;

            switch (status) {
              case 404:
                errorMsg = data.error || "User or column set not found";
                break;
              case 409:
                errorMsg =
                  data.error || "Column set already exists for this user";
                break;
              case 500:
                errorMsg = data.error || "Internal server error occurred";
                break;
              default:
                errorMsg = data.error || "An unexpected error occurred";
            }
          } else {
            // Network error or other issues
            errorMsg =
              "Network error: Please check your connection and try again";
          }

          setErrorMessage(errorMsg);
          setShowErrorModal(true);
          console.error("Error sharing column set:", error);
        })
        .finally(() => {
          setIsLoading(false);
          setLoadingMessage("");
        });
    }
  };

  const onSaveSubmit = async () => {
    if (!selectedColumnSet) {
      setErrorMessage("You must select a column set");
      setShowErrorModal(true);
    } else {
      const accepted = window.confirm(
        `Are you sure you want to save these columns for column set '${selectedColumnSet}'?`
      );

      if (!accepted) return;

      await saveCurrentColumnSet(selectedColumnSet);
    }
  };

  const onNewSubmit = async () => {
    let colSetName = prompt("Enter New Column Set Name");

    if (!colSetName) {
      setErrorMessage("Must provide a name.");
      setShowErrorModal(true);
      return;
    }

    colSetName = colSetName.trim();

    if (columnSets.some((colSet) => colSet.columnSetName === colSetName)) {
      setErrorMessage(
        `Column set ${colSetName} already exists. Please try again`
      );
      setShowErrorModal(true);
      return;
    }

    await saveCurrentColumnSet(colSetName);
    setSelectedColumnSet(colSetName);
  };

  const saveCurrentColumnSet = async (colSetName) => {
    const updatedColumnSets = [
      ...columnSets.filter((item) => item.columnSetName !== colSetName),
      {
        columnSetName: colSetName,
        columns: {
          listingColumns: listingColumns.map((col) => col.id),
          inventoryColumns: inventoryColumns.map((col) => col.id),
        },
      },
    ];

    const requestBody = {
      username: userId,
      columnSets: updatedColumnSets,
    };

    setIsLoading(true);
    setLoadingMessage("Saving column set...");

    try {
      await dvdlocator.put("/nosql/column-sets", requestBody);
      setColumnSets(updatedColumnSets);
      setSuccessMessage("Column set saved successfully");
      setShowSuccessModal(true);
    } catch (err) {
      console.log(err);
      setErrorMessage("Error saving column set");
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const reorderInventoryColumns = (items) => {
    setInventoryColumns(items);
  };

  const reorderlistingColumns = (items) => {
    setListingColumns(items);
  };

  const handleSearchChange = (options, value) => {
    return options.filter((option) => {
      return option.text.toLowerCase().includes(value.toLowerCase());
    });
  };

  // Wait until data loaded to render
  if (!_.isEmpty(columnSets) && userId) {
    return (
      <React.Fragment>
        {/* Loading Modal */}
        <Modal open={isLoading} basic size="small">
          <Modal.Content>
            <Dimmer active inverted>
              <Loader size="large">{loadingMessage}</Loader>
            </Dimmer>
          </Modal.Content>
        </Modal>

        {/* Success Modal */}
        <Modal open={showSuccessModal} size="small">
          <Modal.Header>
            <i className="check circle icon green"></i>
            Success
          </Modal.Header>
          <Modal.Content>
            <p>{successMessage}</p>
          </Modal.Content>
          <Modal.Actions>
            <Button positive onClick={() => setShowSuccessModal(false)}>
              OK
            </Button>
          </Modal.Actions>
        </Modal>

        {/* Error Modal */}
        <Modal open={showErrorModal} size="small">
          <Modal.Header>
            <i className="exclamation triangle icon red"></i>
            Error
          </Modal.Header>
          <Modal.Content>
            <p>{errorMessage}</p>
          </Modal.Content>
          <Modal.Actions>
            <Button negative onClick={() => setShowErrorModal(false)}>
              OK
            </Button>
          </Modal.Actions>
        </Modal>

        <Segment padded className="grid-box">
          <Grid columns={2} stackable textAlign="center">
            <Grid.Row verticalAlign="middle">
              {/* Select Column Set Form */}
              <Grid.Column>
                <div>
                  <div className="ui label">Select Column Set</div>
                  <Form.Field>
                    <Dropdown
                      style={dropdownStyle}
                      search
                      selection
                      value={selectedColumnSet}
                      onChange={(param, data) => displayColumnSet(data.value)}
                      placeholder={"Select column set"}
                      options={columnSets.map((colSet) => ({
                        key: colSet.columnSetName,
                        text: colSet.columnSetName,
                        value: colSet.columnSetName,
                      }))}
                      disabled={isLoading || showSuccessModal || showErrorModal}
                    />
                  </Form.Field>
                </div>

                <button
                  onClick={onSaveSubmit}
                  className="button ui blue inverted"
                  disabled={
                    isLoading ||
                    showSuccessModal ||
                    showErrorModal ||
                    (listingColumns.length < 1 && inventoryColumns.length < 1)
                  }
                >
                  Save
                </button>
                <button
                  onClick={onNewSubmit}
                  disabled={
                    isLoading ||
                    showSuccessModal ||
                    showErrorModal ||
                    (listingColumns.length < 1 && inventoryColumns.length < 1)
                  }
                  className="button ui blue inverted"
                >
                  New
                </button>
                <button
                  onClick={deleteColumnSet}
                  disabled={
                    isLoading ||
                    showSuccessModal ||
                    showErrorModal ||
                    !selectedColumnSet
                  }
                  className="button ui red"
                  title="Delete selected column set"
                >
                  Delete
                </button>
              </Grid.Column>

              {/* Share Column Set Form */}
              <Grid.Column>
                <div>
                  <div className="ui label">Share Column Set</div>
                  <Form.Field>
                    <Dropdown
                      style={dropdownStyle}
                      selection
                      search
                      value={selectedShareRecipient}
                      onChange={(param, data) =>
                        setSelectedShareRecipient(data.value)
                      }
                      placeholder={"Select user"}
                      options={usernames.map((name) => {
                        return {
                          key: name,
                          text: name,
                          value: name,
                        };
                      })}
                      disabled={isLoading || showSuccessModal || showErrorModal}
                    />
                  </Form.Field>
                </div>
                <button
                  onClick={onShareSubmit}
                  disabled={
                    isLoading ||
                    showSuccessModal ||
                    showErrorModal ||
                    !selectedShareRecipient
                  }
                  className="button ui blue inverted"
                >
                  Share
                </button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
        <Segment basic>
          <Grid verticalAlign="middle">
            <Grid.Row>
              <Grid.Column width={2}>
                <Header size="medium">Listing Columns</Header>
              </Grid.Column>
              <Grid.Column width={2}>
                <Form.Field>
                  <Dropdown
                    style={dropdownStyle}
                    search={handleSearchChange}
                    selection
                    fluid
                    value={selectedProductColumn}
                    onChange={(_, data) => {
                      setSelectedProductColumn(data.value);
                    }}
                    placeholder="Select listing column"
                    options={_.map(
                      masterListingColumnSet.filter((item) => item.title),
                      (col) => {
                        let fieldName = extractTooltipOrAltText(col.title);
                        if (!fieldName) {
                          fieldName = formatSQLColumnString(col.data);
                        }
                        return {
                          key: col.data,
                          text: fieldName,
                          value: col.data,
                          disabled: listingColumns.some(
                            (c) => c.id === col.data
                          ),
                        };
                      }
                    )}
                    disabled={isLoading || showSuccessModal || showErrorModal}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={2}>
                <Button
                  inverted
                  primary
                  fluid
                  disabled={
                    isLoading ||
                    showSuccessModal ||
                    showErrorModal ||
                    !selectedProductColumn
                  }
                  onClick={() => {
                    const col = masterListingColumnSet.find(
                      (col) => col.data === selectedProductColumn
                    );
                    addProductColumn(col);
                  }}
                >
                  Add Listing Column
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <ColumnReorder
            items={listingColumns}
            removeColumn={removeProductColumn}
            reorderColumns={reorderlistingColumns}
            getItemStyle={getItemStyle}
            getListStyle={getListStyle}
          />
        </Segment>

        <Segment basic>
          <Grid verticalAlign="middle">
            <Grid.Row>
              <Grid.Column width={2}>
                <Header size="medium">Inventory Columns</Header>
              </Grid.Column>
              <Grid.Column width={2}>
                <Form.Field>
                  <Dropdown
                    style={dropdownStyle}
                    search={handleSearchChange}
                    selection
                    fluid
                    value={selectedInventoryColumn}
                    onChange={(_, data) => {
                      setSelectedInventoryColumn(data.value);
                    }}
                    placeholder="Select inventory column"
                    options={_.map(
                      masterInventoryColumnSet.filter((item) => item.title),
                      (col) => {
                        let fieldName = extractTooltipOrAltText(col.title);
                        if (!fieldName) {
                          fieldName = formatSQLColumnString(col.data);
                        }
                        return {
                          key: col.data,
                          text: fieldName,
                          value: col.data,
                          disabled: inventoryColumns.some(
                            (c) => c.id === col.data
                          ),
                        };
                      }
                    )}
                    disabled={isLoading || showSuccessModal || showErrorModal}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={2}>
                <Button
                  inverted
                  primary
                  fluid
                  disabled={
                    isLoading ||
                    showSuccessModal ||
                    showErrorModal ||
                    !selectedInventoryColumn
                  }
                  onClick={() => {
                    const col = masterInventoryColumnSet.find(
                      (col) => col.data === selectedInventoryColumn
                    );
                    addInventoryColumn(col);
                  }}
                >
                  Add Inventory Column
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <ColumnReorder
            items={inventoryColumns}
            removeColumn={removeInventoryColumn}
            reorderColumns={reorderInventoryColumns}
            getItemStyle={getItemStyle}
            getListStyle={getListStyle}
          />
        </Segment>
      </React.Fragment>
    );
  } else {
    return (
      <div className="ui active transition visible dimmer">
        <div className="content">
          <div className="ui loader"></div>
        </div>
      </div>
    );
  }
};

export default ColumnSets;
